/**
 * Routing Constants
 *
 * Centralized routing configuration for the application.
 * All route definitions should use these constants to ensure consistency.
 */

/**
 * Base path for the application.
 * - Staging: /myra (staging.mmt.ai/myra)
 * - Production: /myra (www.makemytrip.com/myra)
 */
export const BASE_PATH = '/myra' as const;

/**
 * Application routes with base path prefix
 */
export const ROUTES = {
  /** Home/Landing page */
  HOME: BASE_PATH,
  /** Main chat page */
  CHAT: `${BASE_PATH}/chat`,
  /** Thank you page after booking */
  CHAT_THANKYOU: `${BASE_PATH}/chat/thankyou`,
} as const;

/**
 * Route type union derived from ROUTES constant
 */
export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * Check if a path starts with the base path
 */
export function hasBasePath(path: string): boolean {
  return path === BASE_PATH || path.startsWith(`${BASE_PATH}/`);
}

/**
 * Remove base path from a full path
 * @example stripBasePath('/myra/chat') => '/chat'
 */
export function stripBasePath(path: string): string {
  if (path === BASE_PATH) return '/';
  if (path.startsWith(`${BASE_PATH}/`)) {
    return path.slice(BASE_PATH.length);
  }
  return path;
}

/**
 * Add base path to a relative path
 * @example withBasePath('/chat') => '/myra/chat'
 */
export function withBasePath(path: string): string {
  if (path === '/') return BASE_PATH;
  if (path.startsWith('/')) {
    return `${BASE_PATH}${path}`;
  }
  return `${BASE_PATH}/${path}`;
}
